import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { getFirestore, Timestamp, doc, setDoc, collection, getDocs } from "@firebase/firestore";
import { app } from '../../firebase.js';
import Form from 'react-bootstrap/Form';
import profilePictureContImage from '../../Images/defprofile.png';
import { getAuth } from "firebase/auth";
import swal from 'sweetalert';
import Button from 'react-bootstrap/Button';
import { Link, useNavigate } from "react-router-dom";
import Spinner from 'react-bootstrap/Spinner';
import LeftBar from '../../Components/LeftBar'
import Dashnav from '../../Components/Dashnav'
import { logAdminActivity } from '../../utils/activityLogger';
import { buildInvoiceEmail } from '../../utils/emailTemplates';

function AddInvoice() {
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [searchValue, setSearchValue] = useState('');
    const [results, setResults] = useState([]);
    const [studentsIndex, setStudentsIndex] = useState([]);
    const [amount, setAmount] = useState('');
    const [paymentDate, setPaymentDate] = useState('');
    const db = getFirestore(app);
    const auth = getAuth(app);
    const searchResultsRef = useRef(null);
    const lastSearchRef = useRef('');
    const searchLogTimeoutRef = useRef(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    let navigate = useNavigate();

    const formattedAmount = useMemo(() => (
        amount ? Number(amount).toLocaleString('en-KE') : '0'
    ), [amount]);

    const formattedDueDate = useMemo(() => {
        if (!paymentDate) {
            return 'Select a due date';
        }

        const parsedDate = new Date(paymentDate);
        if (Number.isNaN(parsedDate.getTime())) {
            return 'Select a due date';
        }

        return parsedDate.toLocaleString();
    }, [paymentDate]);

    useEffect(() => {
        const fetchStudents = async () => {
            try {
                const querySnapshot = await getDocs(collection(db, "users"));
                const allStudents = [];

                querySnapshot.forEach((doc) => {
                    const data = doc.data();
                    allStudents.push({
                        profilePic: data.profilePic || profilePictureContImage,
                        bio: data.bio || '',
                        userStatus: data.userStatus || '',
                        userId: data.userId,
                        course: data.course || '',
                        userEmail: data.userEmail || '',
                        phoneNumber: data.phoneNumber || ''
                    });
                });

                setStudentsIndex(allStudents);
            } catch (error) {
                console.error("Error loading students for search:", error);
            }
        };

        fetchStudents();
    }, [db]);


    const handleSearchChange = async (event) => {
        const value = event.target.value;
        setSearchValue(value);

        if (searchLogTimeoutRef.current) {
            clearTimeout(searchLogTimeoutRef.current);
        }
        searchLogTimeoutRef.current = setTimeout(() => {
            const trimmed = value.trim();
            if (!trimmed) return;
            if (trimmed === lastSearchRef.current) return;
            lastSearchRef.current = trimmed;
            logAdminActivity({
                action: 'Search',
                details: `Add Invoice search: "${trimmed}"`,
                meta: { page: 'Add Invoice', query: trimmed }
            });
        }, 600);

        const normalizedValue = value.trim().toLowerCase();

        if (normalizedValue !== "") {
            const searchResults = studentsIndex
                .filter((student) => {
                    const name = String(student.bio || '').toLowerCase();
                    const email = String(student.userEmail || '').toLowerCase();
                    const course = String(student.course || '').toLowerCase();
                    const phone = String(student.phoneNumber || '').toLowerCase();

                    return (
                        name.includes(normalizedValue) ||
                        email.includes(normalizedValue) ||
                        course.includes(normalizedValue) ||
                        phone.includes(normalizedValue)
                    );
                })
                .sort((studentA, studentB) => {
                    const nameA = String(studentA.bio || '').toLowerCase();
                    const nameB = String(studentB.bio || '').toLowerCase();
                    const startsA = nameA.startsWith(normalizedValue) ? 0 : 1;
                    const startsB = nameB.startsWith(normalizedValue) ? 0 : 1;

                    if (startsA !== startsB) {
                        return startsA - startsB;
                    }

                    return nameA.localeCompare(nameB);
                })
                .slice(0, 20);

            setResults(searchResults);
        } else {
            setResults([]);
        }
    };

    const onSelectStudent = (userId) => {
        const student = results.find(r => r.userId === userId);
        setSelectedStudent(student);
        setResults([]);
        setSearchValue(student.bio);
    };

    const viewSelectedStudent = useCallback(() => {
        if (!selectedStudent?.userId) {
            return;
        }

        navigate('/view-student', { state: { myData: selectedStudent.userId } });
    }, [navigate, selectedStudent]);

    const handleSubmitIncome = async () => {
        if (!selectedStudent || !amount || !paymentDate) {
            swal("Error", "Please fill in all the required fields", "error");
            return;
        }

        setIsSubmitting(true);

        const paymentTimestamp = new Date(paymentDate).getTime();

        const invoiceNumber = Math.random();
        const generatedReceiptNumner = Math.floor(invoiceNumber * 7971349);

        try {
            const newDataRef = doc(collection(db, "Invoices"));
            await setDoc(newDataRef, {
                studentId: selectedStudent.userId,
                amount: amount,
                dueDate: Timestamp.fromMillis(paymentTimestamp),
                createdBy: auth.currentUser.uid,
                timeCreated: Timestamp.now(),
                paymentId: newDataRef.id,
                invoiceNumber: generatedReceiptNumner
            }).then(() => {
                logAdminActivity({
                    action: 'Send Invoice',
                    details: `Sent invoice to ${selectedStudent.bio} (KES ${amount})`,
                    meta: {
                        page: 'Add Invoice',
                        studentId: selectedStudent.userId,
                        studentName: selectedStudent.bio,
                        amount
                    }
                });


                const emailAddress = selectedStudent.userEmail;
                const emailSubject = "Invoice #" + generatedReceiptNumner;

                const sendEmail = buildInvoiceEmail({
                    studentName: selectedStudent.bio,
                    amount,
                    dueDate: new Date(paymentTimestamp),
                    invoiceNumber: generatedReceiptNumner
                });

                const emailData = {
                    email: emailAddress,
                    message: sendEmail,
                    subject: emailSubject
                };

                const auth = getAuth();
                const user = auth.currentUser;

                user.getIdToken().then((token) => {

                    fetch("https://us-central1-ubunifu-college.cloudfunctions.net/api/send-email", {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            "Authorization": `Bearer ${token}`
                        },
                        body: JSON.stringify(emailData)
                    })
                        .then(res => res.json())
                        .then(data => {


                            const studentsUserName = selectedStudent.bio;
                            const invnumb = generatedReceiptNumner;
                            const phoneNumber = selectedStudent.phoneNumber;

                            sendSMS(studentsUserName, amount, paymentDate, invnumb, phoneNumber, token);

                        })
                        .catch(error => {
                            console.error("Failed to send email:", error);
                        });
                });
            })

        } catch (error) {
            console.error("Error recording income:", error);
            setIsSubmitting(false);
            swal("Error", "Failed to record income", "error");

        }
    };


    const sendSMS = async (studentName, amount, paymentDate, receiptNumber, phoneNumber, token) => {
        const message = `Dear ${studentName}, this is a reminder to pay your fee of KES. ${amount} by ${paymentDate} to avoid learning interruption. View your invoice here https://live.ubunifucollege.com/view-invoice?${receiptNumber}. Thank you. - Nutrition And Dietetics Academy`;
        const smsData = {
            phoneNumber: phoneNumber,
            message: message
        };

        try {
            const response = await fetch('https://us-central1-ubunifu-college.cloudfunctions.net/api/send-sms', {
                method: 'POST',
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify(smsData)
            });

            await response.json();
            swal("Success", "Invoice Sent successfully!", "success");
            navigate("/sent-invoices")
            return true;
        } catch (error) {
            console.error("Error sending SMS: ", error);
            swal("Success", "Invoice Sent successfully!", "success");
            navigate("/sent-invoices");
            return false;
        }
    };


    return (

        <div className='mainContainer'>
            <LeftBar />
            <div className='theMainContainer'>
                <Dashnav />
                <div className='clientsComp addInvoicePage'>
                    <div className="addInvoiceHero">
                        <div className="addInvoiceHeroCopy">
                            <h5>Send an Invoice</h5>
                            <p className="addInvoiceHeroText">
                                Search for a student, confirm their details, then send a clean invoice with the due date and amount.
                            </p>
                        </div>
                        <div className="addInvoiceHeroMeta">
                            <span>{selectedStudent ? 'Student selected' : 'Waiting for student selection'}</span>
                        </div>
                    </div>

                    <div className="addInvoiceLayout">
                        <div className="addInvoiceMainCard">
                            <div className="addInvoiceSectionHeader">
                                <div>
                                    <h6>Student Search</h6>
                                    <p>Find the student you want to invoice.</p>
                                </div>
                                <Link to="/sent-invoices" className="btn btn-outline-dark addInvoiceGhostBtn">
                                    View Sent Invoices
                                </Link>
                            </div>

                            <Form.Group className="mb-0 addInvoiceSearchGroup">
                                <Form.Label>Search for a student</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Type the student's name"
                                    value={searchValue}
                                    onChange={handleSearchChange}
                                />
                                {results.length > 0 && (
                                    <div className='searchResultsStud addInvoiceSearchResults' ref={searchResultsRef}>
                                        {results.map((result, index) => (
                                            <div key={index} className='resultItem' onClick={() => onSelectStudent(result.userId)}>
                                                <div className='resultItemDiv'>
                                                    <img src={result.profilePic} alt={`${result.bio}'s profile`} />
                                                </div>
                                                <div>
                                                    <p>{result.bio}</p>
                                                    <p className='statusResult'>{result.userStatus}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Form.Group>

                            <div className="addInvoiceSelectedCard">
                                <div className="addInvoiceSectionHeader addInvoiceSectionHeaderCompact">
                                    <div>
                                        <h6>Selected Student</h6>
                                        <p>Review the student before sending the invoice.</p>
                                    </div>
                                </div>

                                {selectedStudent ? (
                                    <div className="addInvoiceStudentSummary">
                                        <div className="addInvoiceStudentIdentity">
                                            <div className="addInvoiceStudentAvatar">
                                                <img src={selectedStudent.profilePic || profilePictureContImage} alt={`${selectedStudent.bio}'s profile`} />
                                            </div>
                                            <div>
                                                <h6>{selectedStudent.bio}</h6>
                                                <p>{selectedStudent.userEmail || 'No email recorded'}</p>
                                            </div>
                                        </div>
                                        <div className="addInvoiceStudentMeta">
                                            <span className="addInvoiceMetaPill">{selectedStudent.userStatus || 'Status unavailable'}</span>
                                            <span className="addInvoiceMetaPill">{selectedStudent.course || 'Course unavailable'}</span>
                                            <Button variant="outline-dark" className="addInvoiceGhostBtn" onClick={viewSelectedStudent}>
                                                View student
                                            </Button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="addInvoiceEmptyHint">
                                        Select a student from the search results to continue.
                                    </div>
                                )}
                            </div>

                            <div className="addInvoiceFormCard">
                                <div className="addInvoiceSectionHeader addInvoiceSectionHeaderCompact">
                                    <div>
                                        <h6>Invoice Details</h6>
                                        <p>Set the amount and due date for this invoice.</p>
                                    </div>
                                </div>

                                <div className="addInvoiceFormGrid">
                                    <Form.Group className="mb-0">
                                        <Form.Label>Amount</Form.Label>
                                        <Form.Control
                                            type="number"
                                            placeholder="Enter invoice amount"
                                            value={amount}
                                            onChange={(e) => setAmount(e.target.value)}
                                        />
                                    </Form.Group>

                                    <Form.Group className="mb-0">
                                        <Form.Label>Due Date</Form.Label>
                                        <Form.Control
                                            type="datetime-local"
                                            value={paymentDate}
                                            onChange={(e) => setPaymentDate(e.target.value)}
                                        />
                                    </Form.Group>
                                </div>
                            </div>
                        </div>

                        <div className="addInvoiceSideCard">
                            <div className="addInvoiceSectionHeader addInvoiceSectionHeaderCompact">
                                <div>
                                    <h6>Invoice Preview</h6>
                                    <p>A quick summary before sending.</p>
                                </div>
                            </div>

                            <div className="addInvoicePreviewBlock">
                                <span className="addInvoicePreviewLabel">Student</span>
                                <strong>{selectedStudent?.bio || 'No student selected'}</strong>
                            </div>

                            <div className="addInvoicePreviewBlock">
                                <span className="addInvoicePreviewLabel">Amount</span>
                                <strong>KES {formattedAmount}</strong>
                            </div>

                            <div className="addInvoicePreviewBlock">
                                <span className="addInvoicePreviewLabel">Due Date</span>
                                <strong>{formattedDueDate}</strong>
                            </div>

                            <div className="addInvoicePreviewNote">
                                The invoice will be sent by email and SMS if the student's contact details are available.
                            </div>

                            <Button
                                className='w-100 addInvoiceSubmitBtn'
                                onClick={handleSubmitIncome}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <Spinner
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                        />{' '}
                                        Sending...
                                    </>
                                ) : (
                                    'Send an Invoice'
                                )}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>


    );
}

export default AddInvoice
